/**
 * Users Client
 * Provides contract-based access to user management endpoints.
 * Follows Multi-Endpoint pattern from client factory architecture.
 * @module api/clients/users
 */
import api from "@/services/api"
import { toSameOriginUrl } from "@/services/api"
import { contractRequest, createContractClient } from "../contractClient"
import { XLSX_MEDIA_TYPE } from "@/api/mediaTypes"
import {
  normalizeUsersListResponse,
  normalizeUserDetailsResponse,
  normalizeCreateUserResponse,
  normalizeUpdateUserResponse,
  normalizeDeleteUserResponse,
  normalizeBulkBlockResponse,
  normalizeLoginAsUserResponse,
  normalizeUserFieldsResponse,
} from "../normalizers/users.normalizer"
import {
  LegacyUsersItemsResponseSchema,
  UsersInfoByIdResponseSchema,
  UsersFieldsResponseSchema,
  LegacyCreateUserResponseSchema,
  LegacyBulkBlockResponseSchema,
  LegacyLoginAsUserResponseSchema,
} from "../contracts/users"

const decodeHeaderFilename = (value) => {
  if (typeof value !== "string") {
    return null
  }

  try {
    return decodeURIComponent(value)
  } catch (_error) {
    return null
  }
}

const parseContentDispositionFilename = (contentDisposition) => {
  if (typeof contentDisposition !== "string" || !contentDisposition.trim()) {
    return null
  }

  const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i)
  if (utf8Match) {
    return decodeHeaderFilename(utf8Match[1].trim())
  }

  const basicMatch = contentDisposition.match(
    /filename\s*=\s*(?:"([^"]*)"|([^;]+))/i
  )
  if (!basicMatch) {
    return null
  }

  return (basicMatch[1] || basicMatch[2] || "").trim() || null
}

// LLM:METADATA
// :hierarchy: [Domain:Users | API_Client | Factory]
// :relates-to: uses: "createContractClient", uses: "users normalizers", uses: "RAW schemas", required-by: "useUsers.js", "Users.vue", "UsersAdd.vue"
// :rationale: "Provides type-safe user management API calls with contract validation and response normalization for 9 user endpoints (list, details, CRUD, bulk block/unblock, export, login-as). All 9 endpoints now have RAW schema validation: LegacyUsersItemsResponseSchema (list), LegacyUserDetailsWrapperResponseSchema (details), LegacyCreateUserResponseSchema (create), LegacyUpdateUserResponseSchema (update), LegacyDeleteUserResponseSchema (delete), LegacyBulkBlockResponseSchema (bulk), LegacyExportUsersResponseSchema (export), LegacyLoginAsUserResponseSchema (login-as), UsersFieldsResponseSchema (fields)."
// :contract: pre: "api client initialized", post: "Client object with all user management methods", invariant: "All responses pass RAW schema validation before normalization"
// :decision_cache: "Chose Multi-Endpoint pattern for users due to high endpoint count (9) and diverse operations. Wired RAW schemas for ALL endpoints to enforce RAW-first boundary - removes previous undefined schema usage [USERS-CLIENT-001]"
// :references: SPEC: "Phase 3B Client Factory Implementation", ARCH: "client-factory-architecture.md Pattern 2: Multi-Endpoint"
// LLM:END

/**
 * Create users client with contract validation
 * @returns {Object} Users client with all user management methods
 */
export function createUsersClient() {
  // Users list endpoint with pagination and filtering
  // RAW schema validates snake_case response from backend before normalization
  const usersListClient = createContractClient(
    api,
    "legacy.users.items",
    LegacyUsersItemsResponseSchema,
    normalizeUsersListResponse
  )

  // User details endpoint - RAW schema validates snake_case response
  const userClient = createContractClient(
    api,
    "users/info",
    UsersInfoByIdResponseSchema,
    normalizeUserDetailsResponse
  )

  // Create user endpoint - RAW schema validates snake_case response
  const createUserClient = createContractClient(
    api,
    "users/new",
    LegacyCreateUserResponseSchema,
    normalizeCreateUserResponse
  )

  // Bulk block/unblock endpoint - RAW schema validates snake_case response
  const bulkBlockClient = createContractClient(
    api,
    "users/items_show_hide",
    LegacyBulkBlockResponseSchema,
    normalizeBulkBlockResponse
  )

  // Login as user endpoint - RAW schema validates snake_case response
  const loginAsUserClient = createContractClient(
    api,
    "users/login-as",
    LegacyLoginAsUserResponseSchema,
    normalizeLoginAsUserResponse
  )

  // User fields endpoint (for form definitions)
  // RAW schema validates snake_case response from backend before normalization
  // UsersFieldsResponseSchema wraps array of LegacyUserFieldSchema items
  const userFieldsClient = createContractClient(
    api,
    "users/fields",
    UsersFieldsResponseSchema,
    normalizeUserFieldsResponse
  )

  return {
    /**
     * Get users list with pagination and filtering
     *
     * @param {Object} params - Query parameters
     * @param {string} [params.strFilter] - Search filter string
     * @param {number} [params.page=1] - Page number
     * @param {string} [params.sortField='id'] - Sort field
     * @param {string} [params.sortDir='DESC'] - Sort direction (ASC/DESC)
     * @returns {Promise<Object>} Normalized users list
     * @returns {Array} returns.users - Array of normalized user objects
     * @returns {number} returns.total - Total count of users
     *
     * @example
     * const { users, total } = await usersClient.getUsers({
     *   strFilter: 'test@example.com',
     *   page: 1,
     *   sortField: 'id',
     *   sortDir: 'DESC'
     * })
     */
    getUsers: async (params = {}) => {
      return usersListClient.post({
        strFilter: params.strFilter || "",
        page: params.page || 1,
        sortField: params.sortField || "id",
        sortDir: params.sortDir || "DESC",
      })
    },

    /**
     * Get single user details
     *
     * @param {number} userId - User ID
     * @returns {Promise<Object>} Normalized user details
     * @returns {Object} returns.user - Normalized user object
     *
     * @example
     * const { user } = await usersClient.getUser(123)
     * console.log(user.email, user.name)
     */
    getUser: async (userId) => {
      if (!userId) {
        throw new Error("User ID is required for fetching a user")
      }
      // Use canonical /api/users/info/:id via api.js baseURL "/api".
      return userClient.get(`${userId}`)
    },

    /**
     * Create new user
     *
     * @param {Object} userData - User data to create
     * @param {string} userData.email - User email
     * @param {string} userData.login - User login
     * @param {string} userData.password - User password
     * @param {string} [userData.name] - User name
     * @param {string} [userData.phone] - User phone
     * @param {string} [userData.role] - User role
     * @returns {Promise<Object>} Normalized create response
     * @returns {boolean} returns.success - Success indicator
     * @returns {number} returns.userId - Created user ID
     *
     * @example
     * const result = await usersClient.createUser({
     *   email: 'user@example.com',
     *   login: 'testuser',
     *   password: 'securepass',
     *   name: 'Test User',
     *   role: 'customer'
     * })
     * if (result.success) {
     *   console.log('Created user ID:', result.userId)
     * }
     */
    createUser: async (userData) => {
      return createUserClient.post(userData)
    },

    /**
     * Update existing user via canonical users/edit/:id mutation route.
     *
     * @param {number} userId - User ID to update
     * @param {Object} userData - User data to update
     * @param {string} [userData.email] - User email
     * @param {string} [userData.login] - User login
     * @param {string} [userData.name] - User name
     * @param {string} [userData.phone] - User phone
     * @param {string} [userData.role] - User role
     * @param {boolean} [userData.userBlocked] - Block status
     * @returns {Promise<Object>} Normalized update response
     * @returns {boolean} returns.success - Success indicator
     * @precondition userId is a non-zero numeric identifier accepted by backend users/edit action
     * @postcondition Emits exactly one POST request to `users/edit/${userId}` and no PUT fallback
     * @mutates none
     * @throws {Error} When userId is missing
     */
    updateUser: async (userId, userData) => {
      if (!userId) {
        throw new Error("User ID is required for updating a user")
      }
      return contractRequest({
        api,
        endpoint: `users/edit/${userId}`,
        method: "POST",
        data: userData,
        normalizer: normalizeUpdateUserResponse,
      })
    },

    /**
     * Anonymize selected backend users in one request; orders and points remain.
     * @param {number[]} userIds - Non-empty array of backend user IDs.
     * @returns {Promise<Object>} Normalized success/message response.
     * @throws {Error} Empty/non-array input fails before I/O; transport errors propagate.
     * @mutates none; sends exactly one POST without automatic retries.
     */
    deleteUsers: async (userIds) => {
      if (!Array.isArray(userIds) || userIds.length === 0) {
        throw new Error("User IDs array is required for deleting users")
      }
      return contractRequest({
        api,
        endpoint: "users/delete",
        method: "POST",
        data: { ids: userIds },
        normalizer: normalizeDeleteUserResponse,
      })
    },

    /**
     * Bulk block users
     *
     * @param {number[]} userIds - Array of user IDs to block
     * @returns {Promise<Object>} Normalized bulk block response
     * @returns {boolean} returns.success - Success indicator
     * @returns {number} returns.affectedCount - Number of affected users
     *
     * @example
     * const result = await usersClient.bulkBlock([1, 2, 3])
     * if (result.success) {
     *   console.log(`Blocked ${result.affectedCount} users`)
     * }
     */
    bulkBlock: async (userIds) => {
      if (!Array.isArray(userIds) || userIds.length === 0) {
        throw new Error("User IDs array is required for bulk block")
      }
      return bulkBlockClient.post({
        id: userIds,
        action: "block",
      })
    },

    /**
     * Bulk unblock users
     *
     * @param {number[]} userIds - Array of user IDs to unblock
     * @returns {Promise<Object>} Normalized bulk unblock response
     * @returns {boolean} returns.success - Success indicator
     * @returns {number} returns.affectedCount - Number of affected users
     *
     * @example
     * const result = await usersClient.bulkUnblock([1, 2, 3])
     * if (result.success) {
     *   console.log(`Unblocked ${result.affectedCount} users`)
     * }
     */
    bulkUnblock: async (userIds) => {
      if (!Array.isArray(userIds) || userIds.length === 0) {
        throw new Error("User IDs array is required for bulk unblock")
      }
      return bulkBlockClient.post({
        id: userIds,
        action: "unblock",
      })
    },

    /**
     * Export users to file
     *
     * @param {Object} params - Export parameters
     * @param {Array} [params.fields] - Fields to include in export
     * @param {Object} [params.filters] - Export filters
     * @param {boolean} [params.filters.onlyAgree] - Only users who agreed to rules
     * @returns {Promise<Object>} Normalized export response
     * @returns {boolean} returns.success - Success indicator
     * @returns {Blob} [returns.blob] - Exported file blob
     * @returns {string} [returns.downloadUrl] - Download URL
     *
     * @example
     * const result = await usersClient.exportUsers({
     *   fields: [
     *     { name: 'email', unload: true },
     *     { name: 'name', unload: true }
     *   ],
     *   filters: { onlyAgree: false }
     * })
     * if (result.success && result.blob) {
     *   // Handle blob download
     * }
     */
    exportUsers: async (params = {}) => {
      const response = await api.post(
        toSameOriginUrl("/edit/newreports/users"),
        {
          fields: params.fields || [],
          filters: params.filters || {},
        },
        {
          // Newreports answers JSON to the default Accept list; the export
          // must request the workbook explicitly or the download is JSON.
          headers: { Accept: XLSX_MEDIA_TYPE },
          responseType: "blob",
        }
      )

      return {
        success: response?.data instanceof Blob,
        blob: response?.data,
        filename:
          parseContentDispositionFilename(
            response?.headers?.["content-disposition"]
          ) || "users_export.xlsx",
      }
    },

    /**
     * Login as user (admin impersonation).
     *
     * @param {number} userId - User ID to login as
     * @returns {Promise<Object>} Normalized login response
     * @returns {boolean} returns.success - Success indicator derived from backend
     * raw `status`
     * @returns {string} [returns.loginUrl] - URL to login as user, normalized from
     * raw `redirect_url`
     * @returns {string} [returns.token] - Authentication token
     * @precondition userId is a non-zero backend user identifier
     * @postcondition Emits exactly one POST request to canonical
     * `users/login-as` with JSON body `{ user_id: userId }`
     * @mutates none
     * @throws {Error} When userId is missing
     *
     * @example
     * const result = await usersClient.loginAsUser(123)
     * if (result.success) {
     *   window.location.href = result.loginUrl
     * }
     */
    loginAsUser: async (userId) => {
      if (!userId) {
        throw new Error("User ID is required for login-as-user")
      }
      return loginAsUserClient.post({
        user_id: userId,
      })
    },

    /**
     * Get user fields definition (for forms)
     *
     * @returns {Promise<Object>} Normalized fields response
     * @returns {Array} returns.fields - Array of field definitions
     *
     * @example
     * const { fields } = await usersClient.getUserFields()
     * fields.forEach(field => {
     *   console.log(field.name, field.label, field.type)
     * })
     */
    getUserFields: async () => {
      // Empty string preserves base endpoint "users/fields"
      return userFieldsClient.get("")
    },
  }
}

// Singleton instance for direct import
export const usersClient = createUsersClient()
